import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { BiDatasetAccess, BiDatasetVisibility } from '@euler/model/bi/dataset';
import { BiDatasetRefKind } from '@euler/model/bi/query';
import { Lock, Users } from 'lucide-react-native';
import { memo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BiPageHeader } from '../../components/ui/BiPageHeader';
import { BiRetry } from '../../components/ui/BiRetry';
import { BiTag } from '../../components/ui/BiTag';
import { DashboardChatPanel } from '../../dashboard/chat/DashboardChatPanel';
import { kBiDatasetVisibilityTags } from '../functions/biLibraryText';
import { DatasetActions } from './DatasetActions';
import { DatasetEditPanel } from './DatasetEditPanel';
import { DatasetFacts } from './DatasetFacts';
import { PreviewSection } from './PreviewSection';
import { SchemaSection } from './SchemaSection';
import { UsageSection } from './UsageSection';
import { kBiDatasetChatTool } from './chat/biDatasetChatTool';
import { useBiDatasetChat } from './chat/useBiDatasetChat';
import { useBiDatasetActions } from './functions/useBiDatasetActions';
import { useBiDatasetDetail } from './functions/useBiDatasetDetail';
import { DatasetSharingPanel } from './sharing/DatasetSharingPanel';

/** From this window width the chat sits beside the dataset. */
const kChatBesideFrom = 1024;
/** The dataset page reads best a little narrower than a dashboard. */
const kDetailMaxWidth = 960;

type DatasetPanel = 'edit' | 'share';

/**
 * One dataset of the library: what it is, its fields, its first rows and
 * where it is used; **用 AI 分析** opens its own conversation. A `manage`
 * holder renames, describes, shares and deletes it here.
 */
export const BiDatasetDetailView = memo(
  ({
    datasetRef,
    onBack,
    onOpenDashboard,
    onDeleted,
  }: {
    /** An uploaded dataset id or a built-in model key. */
    datasetRef: string;
    onBack: () => void;
    onOpenDashboard: (dashboardId: string) => void;
    onDeleted: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const compact = useIsMobileLayout();
    const confirm = useConfirm();
    const { width } = useWindowDimensions();
    const chatBeside = width >= kChatBesideFrom;
    const { state, preview, refresh, retryPreview } =
      useBiDatasetDetail(datasetRef);
    const actions = useBiDatasetActions(datasetRef, refresh);
    const chat = useBiDatasetChat(datasetRef);
    const [panel, setPanel] = useState<DatasetPanel | null>(null);
    const detail = state.kind === 'ready' ? state.detail : null;
    const uploaded = detail?.kind === BiDatasetRefKind.Upload ? detail : null;
    const canManage = uploaded?.dataset.permission === BiDatasetAccess.Manage;
    const store = uploaded?.dataset.visibility === BiDatasetVisibility.Store;

    const remove = async () => {
      if (!uploaded) return;
      const ok = await confirm({
        title: '删除数据',
        message: `删除「${uploaded.dataset.name}」后，它就不能再用来提问或做图表。正在看板上使用的数据不能删除。`,
        confirmButtonText: '删除',
        destructive: true,
      });
      if (ok && (await actions.remove())) onDeleted();
    };

    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          backgroundColor: aiColors.background,
        }}
      >
        <View style={{ flex: 1 }}>
          <BiPageHeader
            title={detail ? biText(detail.catalog.label) : '数据'}
            onBack={onBack}
            badge={
              uploaded ? (
                <BiTag
                  text={kBiDatasetVisibilityTags[uploaded.dataset.visibility]}
                  color={store ? aiColors.green : aiColors.mutedForeground}
                  icon={store ? Users : Lock}
                />
              ) : detail ? (
                <BiTag text="内置" color={aiColors.blue} />
              ) : null
            }
            actions={
              detail ? (
                <DatasetActions
                  compact={compact}
                  canManage={canManage}
                  showAnalyze={!(chatBeside && chat.visible)}
                  deleting={actions.deleting}
                  onAnalyze={() => chat.setVisible(true)}
                  onEdit={() => {
                    actions.clearSaveError();
                    setPanel('edit');
                  }}
                  onShare={() => setPanel('share')}
                  onDelete={() => void remove()}
                />
              ) : null
            }
          />
          {!detail ? (
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {state.kind === 'loading' ? (
                <ActivityIndicator color={aiColors.primary} />
              ) : state.kind === 'missing' ? (
                <BiRetry
                  label="数据不存在，或你没有使用权限"
                  onRetry={onBack}
                />
              ) : (
                <BiRetry onRetry={refresh} />
              )}
            </View>
          ) : (
            <ScrollView
              contentContainerStyle={{
                padding: rpx(16),
                paddingBottom: insets.bottom + rpx(32),
              }}
            >
              <View
                style={{
                  width: '100%',
                  maxWidth: kDetailMaxWidth,
                  alignSelf: 'center',
                  gap: rpx(16),
                }}
              >
                {actions.deleteError ? (
                  <View
                    style={{
                      padding: rpx(12),
                      borderRadius: rpx(12),
                      backgroundColor: aiColors.card,
                      borderWidth: 1,
                      borderColor: aiColors.destructive,
                    }}
                  >
                    <CardNotice tone="danger" text={actions.deleteError} />
                  </View>
                ) : null}
                <DatasetFacts detail={detail} />
                {uploaded ? (
                  <UsageSection
                    usage={uploaded.usageDetail}
                    onOpenDashboard={onOpenDashboard}
                  />
                ) : null}
                <SchemaSection catalog={detail.catalog} />
                {uploaded ? (
                  <PreviewSection
                    state={preview}
                    rowCount={uploaded.dataset.rowCount}
                    onRetry={retryPreview}
                  />
                ) : null}
              </View>
            </ScrollView>
          )}
          {uploaded && canManage ? (
            <>
              <DatasetEditPanel
                visible={panel === 'edit'}
                dataset={uploaded.dataset}
                busy={actions.saving}
                error={actions.saveError}
                onSave={async request => {
                  if (await actions.update(request)) setPanel(null);
                }}
                onClose={() => setPanel(null)}
              />
              <DatasetSharingPanel
                visible={panel === 'share'}
                dataset={uploaded.dataset}
                onSaved={refresh}
                onClose={() => setPanel(null)}
              />
            </>
          ) : null}
        </View>
        {chat.visible || chat.opened ? (
          <DashboardChatPanel
            tool={kBiDatasetChatTool}
            side={chatBeside}
            visible={chat.visible}
            conversation={chat.conversation}
            state={chat.state}
            tapSettled$={chat.conversation.tapSettled$}
            onClose={() => chat.setVisible(false)}
          />
        ) : null}
      </View>
    );
  },
);
